import { Link } from "react-router-dom";
import type { ActivityRow } from "../api/types";
import { relativeTime } from "../lib/activity";

// spec-8 — the ranked engagement board's table body, shared by the Live view
// (DashboardPage) and the per-org rollup (OrgDetailPage) so both render the SAME
// row shape (ac-3/ac-23 UI parts). Rows arrive already filtered + sorted by the
// caller. The full set is rendered — emails untruncated, no pagination (ac-6 UI
// part, agent-legible). Columns: busyness bar + 7-day velocity sparkline, user,
// org + org-aggregate busyness, last action + how-long-ago, MCP traffic, volume,
// action-diversity badge, active-days, activation counts, cohort, tags, and a
// going-quiet amber badge (ac-8/ac-9/ac-10).

// Inline SVG sparkline of the 7-day daily velocity — no chart dependency.
export function Sparkline({ values, width = 64, height = 18 }: { values: number[]; width?: number; height?: number }) {
  const max = Math.max(1, ...values);
  const n = Math.max(1, values.length);
  const gap = 1.5;
  const barW = (width - gap * (n - 1)) / n;
  return (
    <svg width={width} height={height} data-testid="sparkline" aria-hidden className="block">
      {values.map((v, i) => {
        const h = Math.max(1, (v / max) * height);
        return (
          <rect
            key={i}
            x={i * (barW + gap)}
            y={height - h}
            width={barW}
            height={h}
            rx={0.5}
            className="fill-accent"
            opacity={v === 0 ? 0.2 : 0.85}
          />
        );
      })}
    </svg>
  );
}

// Horizontal busyness bar, normalised against the busiest row in view.
function BusynessBar({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="h-1.5 w-full rounded-full bg-edge" data-testid="busyness-bar" title={`busyness ${value.toFixed(2)}`}>
      <div className="h-full rounded-full bg-accent" style={{ width: `${pct}%` }} />
    </div>
  );
}

const COHORT_LABEL: Record<string, string> = {
  this_week: "this week",
  last_week: "last week",
  this_month: "this month",
  older: "older",
};

export function ActivityTable({ rows }: { rows: ActivityRow[] }) {
  const maxBusyness = Math.max(0, ...rows.map((r) => r.busyness));
  return (
    <div className="overflow-x-auto rounded-xl border border-edge">
      <table className="w-full text-sm" data-testid="activity-table">
        <thead className="border-b border-edge bg-surface">
          <tr className="text-left text-xs font-medium uppercase tracking-wider text-muted">
            <th className="px-4 py-3">Activity</th>
            <th className="px-4 py-3">User</th>
            <th className="px-4 py-3">Org</th>
            <th className="px-4 py-3">Last action</th>
            <th className="px-4 py-3">MCP</th>
            <th className="px-4 py-3 text-right">Volume</th>
            <th className="px-4 py-3 text-center">Diversity</th>
            <th className="px-4 py-3 text-right">Active days</th>
            <th className="px-4 py-3 text-right">Activation</th>
            <th className="px-4 py-3">Cohort</th>
            <th className="px-4 py-3">Tags</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((u) => (
            <tr
              key={u.id}
              data-testid="activity-row"
              data-user-id={u.id}
              data-busyness={u.busyness}
              className="border-b border-edge-subtle align-top last:border-0 hover:bg-card-hover"
            >
              <td className="w-40 px-4 py-3">
                <div className="flex flex-col gap-1.5">
                  <BusynessBar value={u.busyness} max={maxBusyness} />
                  <Sparkline values={u.velocity} />
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <Link to={`/users/${u.id}`} data-testid="activity-user" className="font-medium text-accent hover:underline">
                    {u.name ?? "Unknown"}
                  </Link>
                  {u.goingQuiet && (
                    <span
                      data-testid="going-quiet-badge"
                      title="Was active in the prior week but has gone quiet this week."
                      className="rounded-full border border-amber-500/40 bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-600 dark:text-amber-400"
                    >
                      Going quiet
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted">{u.email}</div>
              </td>
              <td className="px-4 py-3 text-secondary">
                {u.orgs.length ? (
                  <>
                    <Link to={`/orgs/${u.orgs[0].orgId}`} data-testid="activity-org" className="text-accent hover:underline">
                      {u.orgs[0].orgName}
                    </Link>
                    {u.orgBusyness !== null && (
                      <span className="ml-1.5 text-xs text-muted" data-testid="activity-org-busyness" title="Org aggregate busyness">
                        ({u.orgBusyness.toFixed(1)})
                      </span>
                    )}
                  </>
                ) : (
                  "—"
                )}
              </td>
              <td className="px-4 py-3 text-secondary">
                {u.lastAction ? (
                  <>
                    <div>{u.lastAction.label}</div>
                    <div className="text-xs text-muted" data-testid="last-action-ago">
                      {relativeTime(u.lastAction.at)}
                    </div>
                  </>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </td>
              <td className="px-4 py-3 text-secondary" data-testid="mcp-traffic">
                <div className="tabular-nums">{u.mcp.count}</div>
                <div className="text-xs text-muted">
                  {u.mcp.lastCallAt ? relativeTime(u.mcp.lastCallAt) : u.mcpConnected ? "connected" : "—"}
                </div>
              </td>
              <td className="px-4 py-3 text-right tabular-nums">{u.volume}</td>
              <td className="px-4 py-3 text-center">
                <span
                  data-testid="diversity-badge"
                  className="rounded-md border border-edge bg-surface px-1.5 py-0.5 text-xs tabular-nums text-secondary"
                  title="Distinct action types used this week (out of 5)."
                >
                  {u.actionDiversity}/5
                </span>
              </td>
              <td className="px-4 py-3 text-right tabular-nums">{u.activeDays}</td>
              <td className="px-4 py-3 text-right text-xs text-secondary tabular-nums" data-testid="activation-counts">
                {u.specCount}s / {u.decisionCount}d / {u.acCount}a
              </td>
              <td className="px-4 py-3 text-xs text-muted" data-testid="cohort-cell">
                {COHORT_LABEL[u.cohort] ?? u.cohort}
              </td>
              <td className="px-4 py-3">
                {u.tags.length ? (
                  <div className="flex flex-wrap gap-1">
                    {u.tags.map((t) => (
                      <span
                        key={`${t.scope}:${t.value}`}
                        className="rounded-md border border-edge bg-surface px-1.5 py-0.5 text-[10px] text-secondary"
                      >
                        {t.scope}:{t.value}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-xs text-muted">—</span>
                )}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={11} className="px-4 py-8 text-center text-muted">
                No activity matches the current filters.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
